import React, { useState, useEffect, useCallback } from 'react';
import { apiGet } from '../api';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  BarChart3, DollarSign, Users, Ticket, CalendarDays, TrendingUp, 
  Loader2, RefreshCw, User, Download, CreditCard, Receipt, Banknote, Building
} from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/apiError';

// Generate month options for last 12 months
const generateMonthOptions = () => {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('en-IE', { month: 'long', year: 'numeric' });
    options.push({ value, label });
  }
  return options;
};

export const StudioReportsPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('monthly');

  // Report data
  const [revenueReport, setRevenueReport] = useState(null);
  const [bookingsReport, setBookingsReport] = useState(null);
  const [clientsReport, setClientsReport] = useState(null);
  const [classpassReport, setClasspassReport] = useState(null);
  const [instructorsReport, setInstructorsReport] = useState(null);
  
  // Payment processing report
  const [paymentsReport, setPaymentsReport] = useState(null);
  const [paymentMonth, setPaymentMonth] = useState(generateMonthOptions()[0]?.value || '');
  const [paymentLoading, setPaymentLoading] = useState(false);

  // Payouts report (bank reconciliation)
  const [payoutsReport, setPayoutsReport] = useState(null);
  const [payoutMonth, setPayoutMonth] = useState(generateMonthOptions()[0]?.value || '');
  const [payoutLoading, setPayoutLoading] = useState(false);

  // Refunds centralised view
  const [refundsData, setRefundsData] = useState(null);
  const [refundsLoading, setRefundsLoading] = useState(false);
  const [refundStatusFilter, setRefundStatusFilter] = useState('all');
  const [refundDateFilter, setRefundDateFilter] = useState('30d');

  const fetchRefunds = useCallback(async () => {
    setRefundsLoading(true);
    try {
      const query = {};
      if (refundStatusFilter !== 'all') query.status = refundStatusFilter;
      if (refundDateFilter !== 'all') {
        const now = new Date();
        const days = refundDateFilter === '7d' ? 7 : refundDateFilter === '30d' ? 30 : 90;
        const since = new Date(now.getTime() - days * 86400000);
        query.start_date = since.toISOString().split('T')[0];
      }
      const data = await apiGet('/api/studio/refunds', { query });
      setRefundsData(data);
    } catch (e) {
      const msg = formatApiError(e, 'Failed to load refunds');
      toast.error(msg);
    } finally {
      setRefundsLoading(false);
    }
  }, [refundStatusFilter, refundDateFilter]);

  const fetchAllReports = useCallback(async () => {
    setLoading(true);
    try {
      const [revenue, bookings, clients, classpasses, instructors] = await Promise.all([
        apiGet('/api/studio/reports/revenue', { query: { period: period } }),
        apiGet('/api/studio/reports/bookings'),
        apiGet('/api/studio/reports/clients'),
        apiGet('/api/studio/reports/classpasses'),
        apiGet('/api/studio/reports/instructors')
      ]);
      setRevenueReport(revenue);
      setBookingsReport(bookings);
      setClientsReport(clients);
      setClasspassReport(classpasses);
      setInstructorsReport(instructors);
    } catch (e) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [period]);

  const fetchPaymentReport = useCallback(async () => {
    setPaymentLoading(true);
    try {
      const data = await apiGet('/api/studio/reports/payments', { query: { month: paymentMonth } });
      setPaymentsReport(data);
    } catch (e) {
      toast.error('Failed to load payment report');
    } finally {
      setPaymentLoading(false);
    }
  }, [paymentMonth]);

  const fetchPayoutReport = useCallback(async () => {
    setPayoutLoading(true);
    try {
      const data = await apiGet('/api/studio/reports/payouts', { query: { month: payoutMonth } });
      setPayoutsReport(data);
    } catch (e) {
      toast.error('Failed to load payout report');
    } finally {
      setPayoutLoading(false);
    }
  }, [payoutMonth]);

  useEffect(() => {
    fetchAllReports();
  }, [fetchAllReports]);

  useEffect(() => {
    if (paymentMonth) {
      fetchPaymentReport();
    }
  }, [paymentMonth, fetchPaymentReport]);

  useEffect(() => {
    if (payoutMonth) {
      fetchPayoutReport();
    }
  }, [payoutMonth, fetchPayoutReport]);

  useEffect(() => {
    fetchRefunds();
  }, [fetchRefunds]);

  const exportPaymentsToCSV = () => {
    if (!paymentsReport?.payments?.length) {
      toast.error('No payment data to export');
      return;
    }

    const headers = ['Date', 'Client Name', 'Payment Session ID', 'Description', 'Amount (€)', 'Processing Fee (€)', 'Platform Fee (€)', 'Net Sale (€)', 'Status', 'Fees Source'];
    const rows = paymentsReport.payments.map(p => [
      new Date(p.date).toLocaleDateString('en-IE'),
      p.client_name,
      p.payment_session_id,
      p.description,
      p.amount.toFixed(2),
      p.processing_fee.toFixed(2),
      p.platform_fee.toFixed(2),
      p.net_sale.toFixed(2),
      p.status,
      p.fees_source || 'estimated'
    ]);

    // Add summary row
    rows.push([]);
    rows.push(['Summary']);
    rows.push(['Total Transactions', paymentsReport.summary.total_transactions]);
    rows.push(['Total Amount', '', '', '', paymentsReport.summary.total_amount.toFixed(2)]);
    rows.push(['Total Processing Fees', '', '', '', '', paymentsReport.summary.total_processing_fees.toFixed(2)]);
    rows.push(['Total Platform Fees', '', '', '', '', '', paymentsReport.summary.total_platform_fees.toFixed(2)]);
    rows.push(['Total Net Sales', '', '', '', '', '', '', paymentsReport.summary.total_net_sales.toFixed(2)]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `payment-report-${paymentMonth}.csv`;
    link.click();
    toast.success('Payment report exported');
  };

  const exportPayoutsToCSV = () => {
    if (!payoutsReport?.payouts?.length) {
      toast.error('No payout data to export');
      return;
    }

    const headers = ['Date', 'Payout ID', 'Amount (€)', 'Currency', 'Status', 'Bank Reference'];
    const rows = payoutsReport.payouts.map(p => [
      p.date ? new Date(p.date).toLocaleDateString('en-IE') : '',
      p.payout_id,
      p.amount.toFixed(2),
      p.currency,
      p.status,
      p.bank_reference || ''
    ]);

    // Add summary row
    rows.push([]);
    rows.push(['Summary']);
    rows.push(['Total Payouts', payoutsReport.summary.total_payouts]);
    rows.push(['Total Amount', '', payoutsReport.summary.total_amount.toFixed(2)]);
    rows.push(['Pending Amount', '', payoutsReport.summary.pending_amount.toFixed(2)]);
    rows.push(['Completed Amount', '', payoutsReport.summary.completed_amount.toFixed(2)]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `payout-report-${payoutMonth}.csv`;
    link.click();
    toast.success('Payout report exported');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="studio-reports-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Reports</h1>
            <p className="text-muted-foreground">Analytics and insights for your studio</p>
          </div>
          <div className="flex gap-2">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Last 30 Days</SelectItem>
                <SelectItem value="weekly">Last 12 Weeks</SelectItem>
                <SelectItem value="monthly">Last 12 Months</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchAllReports}>
              <RefreshCw className="w-4 h-4 mr-2" />Refresh
            </Button>
          </div>
        </div>

        <Tabs defaultValue="revenue" className="space-y-6">
          <TabsList>
            <TabsTrigger value="revenue">Revenue</TabsTrigger>
            <TabsTrigger value="payments">Payments</TabsTrigger>
            <TabsTrigger value="payouts">Payouts</TabsTrigger>
            <TabsTrigger value="bookings">Bookings</TabsTrigger>
            <TabsTrigger value="clients">Clients</TabsTrigger>
            <TabsTrigger value="classpasses">ClassPasses</TabsTrigger>
            <TabsTrigger value="instructors">Instructors</TabsTrigger>
            <TabsTrigger value="refunds" data-testid="reports-refunds-tab">Refunds</TabsTrigger>
          </TabsList>

          {/* Revenue Report */}
          <TabsContent value="revenue">
            <div className="grid md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total Revenue</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-primary">€{revenueReport?.total_revenue?.toFixed(2) || '0.00'}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Platform Fees</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">€{revenueReport?.platform_fees?.toFixed(2) || '0.00'}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Net Revenue</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-emerald-600">€{revenueReport?.net_revenue?.toFixed(2) || '0.00'}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Transactions</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">{revenueReport?.transaction_count || 0}</p>
                </CardContent>
              </Card>
            </div>

            {revenueReport?.by_type && Object.keys(revenueReport.by_type).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Revenue by Type</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(revenueReport.by_type).map(([type, amount]) => (
                      <div key={type} className="flex items-center justify-between">
                        <span className="capitalize">{type.replace(/_/g, ' ')}</span>
                        <span className="font-semibold">€{amount.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Payments Report */}
          <TabsContent value="payments">
            <div className="space-y-6">
              {/* Month Filter and Export */}
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium">Filter by Month:</label>
                  <Select value={paymentMonth} onValueChange={setPaymentMonth}>
                    <SelectTrigger className="w-48" data-testid="payment-month-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {generateMonthOptions().map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  variant="outline" 
                  onClick={exportPaymentsToCSV}
                  disabled={!paymentsReport?.payments?.length}
                  data-testid="export-csv-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>

              {/* Summary Cards */}
              <div className="grid md:grid-cols-5 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Total Transactions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">{paymentsReport?.summary?.total_transactions || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Total Amount</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-primary">€{paymentsReport?.summary?.total_amount?.toFixed(2) || '0.00'}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Processing Fees</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-amber-600">€{paymentsReport?.summary?.total_processing_fees?.toFixed(2) || '0.00'}</p>
                    <p className="text-xs text-muted-foreground">1.3% + €0.10</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Platform Fees</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">€{paymentsReport?.summary?.total_platform_fees?.toFixed(2) || '0.00'}</p>
                    <p className="text-xs text-muted-foreground">1% of transaction</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Net Sales</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-emerald-600">€{paymentsReport?.summary?.total_net_sales?.toFixed(2) || '0.00'}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Payment Transactions Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Receipt className="w-5 h-5" />
                    Payment Transactions
                  </CardTitle>
                  <CardDescription>
                    Detailed breakdown of client payments including fees
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {paymentLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                  ) : paymentsReport?.payments?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Client</TableHead>
                            <TableHead>Payment ID</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                            <TableHead className="text-right">Processing Fee</TableHead>
                            <TableHead className="text-right">Platform Fee</TableHead>
                            <TableHead className="text-right">Net Sale</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paymentsReport.payments.map((payment, i) => (
                            <TableRow key={payment.payment_session_id || i} data-testid={`payment-row-${i}`}>
                              <TableCell className="font-medium">
                                {new Date(payment.date).toLocaleDateString('en-IE')}
                              </TableCell>
                              <TableCell>{payment.client_name}</TableCell>
                              <TableCell className="font-mono text-xs text-muted-foreground">
                                {payment.payment_session_id.slice(0, 20)}...
                              </TableCell>
                              <TableCell className="text-right">€{payment.amount.toFixed(2)}</TableCell>
                              <TableCell className="text-right text-amber-600">€{payment.processing_fee.toFixed(2)}</TableCell>
                              <TableCell className="text-right">€{payment.platform_fee.toFixed(2)}</TableCell>
                              <TableCell className="text-right font-semibold text-emerald-600">€{payment.net_sale.toFixed(2)}</TableCell>
                              <TableCell>
                                <Badge variant={payment.status === 'completed' || payment.status === 'captured' ? 'default' : 'secondary'}>
                                  {payment.status}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>No payment transactions for this period</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Payouts Report (Bank Reconciliation) */}
          <TabsContent value="payouts">
            <div className="space-y-6">
              {/* Month Filter and Export */}
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium">Filter by Month:</label>
                  <Select value={payoutMonth} onValueChange={setPayoutMonth}>
                    <SelectTrigger className="w-48" data-testid="payout-month-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {generateMonthOptions().map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  variant="outline" 
                  onClick={exportPayoutsToCSV}
                  disabled={!payoutsReport?.payouts?.length}
                  data-testid="export-payouts-csv-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>

              {/* Summary Cards */}
              <div className="grid md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Total Payouts</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">{payoutsReport?.summary?.total_payouts || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Total Amount</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-primary">€{payoutsReport?.summary?.total_amount?.toFixed(2) || '0.00'}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Pending</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-amber-600">€{payoutsReport?.summary?.pending_amount?.toFixed(2) || '0.00'}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Completed</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-emerald-600">€{payoutsReport?.summary?.completed_amount?.toFixed(2) || '0.00'}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Info message if no sub-account */}
              {payoutsReport?.message && (
                <Card className="border-amber-200 bg-amber-50">
                  <CardContent className="py-4">
                    <div className="flex items-center gap-3 text-amber-800">
                      <Building className="w-5 h-5" />
                      <p>{payoutsReport.message}</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Payouts Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Banknote className="w-5 h-5" />
                    Bank Payouts
                  </CardTitle>
                  <CardDescription>
                    Payouts to your bank account for reconciliation
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {payoutLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                  ) : payoutsReport?.payouts?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Payout ID</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                            <TableHead>Currency</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Bank Reference</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {payoutsReport.payouts.map((payout, i) => (
                            <TableRow key={payout.payout_id || i} data-testid={`payout-row-${i}`}>
                              <TableCell className="font-medium">
                                {payout.date ? new Date(payout.date).toLocaleDateString('en-IE') : '-'}
                              </TableCell>
                              <TableCell className="font-mono text-xs text-muted-foreground">
                                {payout.payout_id.slice(0, 25)}...
                              </TableCell>
                              <TableCell className="text-right font-semibold">€{payout.amount.toFixed(2)}</TableCell>
                              <TableCell>{payout.currency}</TableCell>
                              <TableCell>
                                <Badge variant={
                                  payout.status === 'Completed' ? 'default' : 
                                  payout.status === 'Pending' || payout.status === 'Processing' ? 'secondary' : 
                                  payout.status === 'Returned' ? 'destructive' : 'outline'
                                }>
                                  {payout.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="font-mono text-xs">
                                {payout.bank_reference || '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <Banknote className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>No payouts for this period</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Bookings Report */}
          <TabsContent value="bookings">
            <div className="grid md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total Bookings</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">{bookingsReport?.total_bookings || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Confirmed</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-emerald-600">{bookingsReport?.confirmed || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Cancelled</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-amber-600">{bookingsReport?.cancelled || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Cancellation Rate</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">{bookingsReport?.cancellation_rate || 0}%</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Popular Classes</CardTitle>
                <CardDescription>Top 10 classes by booking volume</CardDescription>
              </CardHeader>
              <CardContent>
                {bookingsReport?.popular_classes?.length > 0 ? (
                  <div className="space-y-3">
                    {bookingsReport.popular_classes.map((cls, i) => (
                      <div key={i} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="text-lg font-bold text-muted-foreground">#{i + 1}</span>
                          <span className="font-medium">{cls.name}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span>{cls.total} bookings</span>
                          <Badge variant="outline">{cls.confirmed} confirmed</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No booking data yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Clients Report */}
          <TabsContent value="clients">
            <div className="grid md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total Clients</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">{clientsReport?.total_clients || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">New (30 days)</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-emerald-600">{clientsReport?.new_clients_30d || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Active (30 days)</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-blue-600">{clientsReport?.active_clients_30d || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Retention Rate</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <p className="text-2xl font-bold">{clientsReport?.retention_rate || 0}%</p>
                    <TrendingUp className="w-5 h-5 text-emerald-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Client Health</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span>Active Clients</span>
                    <span>{clientsReport?.active_clients_30d || 0} / {clientsReport?.total_clients || 0}</span>
                  </div>
                  <Progress value={clientsReport?.retention_rate || 0} />
                </div>
                <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                  <p className="text-amber-800 font-medium">{clientsReport?.inactive_clients_90d || 0} inactive clients</p>
                  <p className="text-amber-700 text-sm">Haven't booked in over 90 days</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ClassPass Report */}
          <TabsContent value="classpasses">
            <div className="grid md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Passes Sold</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">{classpassReport?.total_passes_sold || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Active</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-emerald-600">{classpassReport?.active || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Credits Sold</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">{classpassReport?.total_credits_sold || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Redemption Rate</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">{classpassReport?.redemption_rate || 0}%</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>ClassPass Status Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 rounded-lg bg-emerald-50">
                    <p className="text-3xl font-bold text-emerald-600">{classpassReport?.active || 0}</p>
                    <p className="text-sm text-emerald-700">Active</p>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-gray-50">
                    <p className="text-3xl font-bold text-gray-600">{classpassReport?.exhausted || 0}</p>
                    <p className="text-sm text-gray-700">Exhausted</p>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-amber-50">
                    <p className="text-3xl font-bold text-amber-600">{classpassReport?.expired || 0}</p>
                    <p className="text-sm text-amber-700">Expired</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Instructors Report */}
          <TabsContent value="instructors">
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Total Instructors: {instructorsReport?.total_instructors || 0}</CardTitle>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Instructor Performance</CardTitle>
              </CardHeader>
              <CardContent>
                {instructorsReport?.instructor_stats?.length > 0 ? (
                  <div className="space-y-3">
                    {instructorsReport.instructor_stats.map((inst, i) => (
                      <div key={inst.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="w-5 h-5 text-primary" />
                          </div>
                          <span className="font-medium">{inst.name}</span>
                        </div>
                        <div className="flex items-center gap-6 text-sm">
                          <div className="text-center">
                            <p className="font-semibold">{inst.classes_scheduled}</p>
                            <p className="text-muted-foreground">Classes</p>
                          </div>
                          <div className="text-center">
                            <p className="font-semibold">{inst.total_attendees}</p>
                            <p className="text-muted-foreground">Attendees</p>
                          </div>
                          <div className="text-center">
                            <p className="font-semibold">{inst.avg_class_size}</p>
                            <p className="text-muted-foreground">Avg Size</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No instructor data yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Refunds Report — centralised view */}
          <TabsContent value="refunds">
            <div className="flex flex-wrap gap-3 items-center mb-6" data-testid="refunds-filter-bar">
              <div className="flex items-center gap-2">
                <Receipt className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Status:</span>
                <Select value={refundStatusFilter} onValueChange={setRefundStatusFilter}>
                  <SelectTrigger className="w-40" data-testid="refunds-status-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="succeeded">Succeeded</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Period:</span>
                <Select value={refundDateFilter} onValueChange={setRefundDateFilter}>
                  <SelectTrigger className="w-40" data-testid="refunds-period-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7d">Last 7 days</SelectItem>
                    <SelectItem value="30d">Last 30 days</SelectItem>
                    <SelectItem value="90d">Last 90 days</SelectItem>
                    <SelectItem value="all">All time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" size="sm" onClick={fetchRefunds} data-testid="refunds-refresh-btn">
                <RefreshCw className="w-4 h-4 mr-2" />Refresh
              </Button>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <Card data-testid="refunds-kpi-total-count">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total Refunds</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-primary">{refundsData?.total_count || 0}</p>
                  <p className="text-xs text-muted-foreground">in selected period</p>
                </CardContent>
              </Card>
              <Card data-testid="refunds-kpi-total-refunded">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Refunded (succeeded)</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-red-600">
                    €{((refundsData?.total_refunded_cents || 0) / 100).toFixed(2)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {refundsData?.currency || 'EUR'} — excludes pending/failed
                  </p>
                </CardContent>
              </Card>
              <Card data-testid="refunds-kpi-avg">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Avg Refund</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-muted-foreground">
                    €{refundsData?.total_count ? ((refundsData.total_refunded_cents / refundsData.total_count) / 100).toFixed(2) : '0.00'}
                  </p>
                  <p className="text-xs text-muted-foreground">per refund</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Refund History</CardTitle>
                <CardDescription>
                  Every refund initiated from this studio — issued via RyftPay and logged here.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {refundsLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin" data-testid="refunds-loading" />
                  </div>
                ) : !refundsData?.refunds?.length ? (
                  <div className="text-center py-12" data-testid="refunds-empty">
                    <Receipt className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                    <p className="text-muted-foreground">No refunds in this period</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Adjust the filters above or initiate a refund from the Payments tab.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table data-testid="refunds-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Client</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Reason</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Processed by</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {refundsData.refunds.map((r, i) => (
                          <TableRow key={r.id} data-testid={`refund-row-${i}`}>
                            <TableCell className="text-sm text-muted-foreground">
                              {r.created_at ? new Date(r.created_at).toLocaleDateString('en-IE', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <div className="font-medium">{r.user_name || '—'}</div>
                                <div className="text-muted-foreground text-xs">{r.user_email || ''}</div>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                              {r.description || r.transaction_id || '—'}
                            </TableCell>
                            <TableCell className="font-medium">€{(r.amount / 100).toFixed(2)}</TableCell>
                            <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                              {r.reason || '—'}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  ['succeeded', 'success', 'completed'].includes(String(r.status).toLowerCase())
                                    ? 'default'
                                    : String(r.status).toLowerCase() === 'pending'
                                    ? 'secondary'
                                    : 'destructive'
                                }
                                data-testid={`refund-status-${i}`}
                              >
                                {r.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {r.initiated_by_name || '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};
